<template>
  <SectionCard title="Handpenningslån">
    <div class="card-columns">
      <!-- Lånebelopp -->
      <DataSection title="Lånebelopp">
        <DataField label="Nuvarande kapitalskuld">
          {{ formatAmountWithCurrency(depositLoan.principal.current) }}
        </DataField>
        <DataField label="Ursprunglig kapitalskuld">
          {{ formatAmountWithCurrency(depositLoan.principal.original) }}
        </DataField>
      </DataSection>

      <!-- Ränta -->
      <DataSection title="Ränta">
        <DataField label="Aktuell ränta">{{ depositLoan.pricing.interestRate }} %</DataField>
        <DataField label="Effektiv ränta">{{ depositLoan.pricing.effectiveInterestRate }} %</DataField>
      </DataSection>

      <!-- Räntebindning -->
      <DataSection title="Räntebindning">
        <DataField label="Räntebindningsperiod">
          {{ t(`INTEREST_TERM.${depositLoan.interestTerm.interestTerm}`) }}
        </DataField>
      </DataSection>

      <!-- Datum -->
      <DataSection title="Datum">
        <DataField label="Utbetalningsdag">{{ formatDateIfValid(depositLoan.startDate) ?? '-' }}</DataField>
        <DataField label="Planerat återbetalningsdatum">
          {{ formatDateIfValid(depositLoan.plannedRepaymentDate) ?? '-' }}
        </DataField>
        <DataField label="Avslutsdatum">{{ formatDateIfValid(depositLoan.endDate) ?? '-' }}</DataField>
      </DataSection>

      <!-- Objekt -->
      <DataSection title="Objekt">
        <DataField label="Typ av objekt">{{ collateralTypeLabel }}</DataField>
        <DataField label="Adress">{{ fullAddress }}</DataField>
      </DataSection>

      <!-- Lånenummer -->
      <DataSection title="Lånenummer">
        <DataField label="Lånenummer">{{ depositLoan.publicLoanNbr }}</DataField>
        <DataField label="Lånets ID">{{ depositLoan.id }}</DataField>
      </DataSection>
    </div>
  </SectionCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { SectionCard } from '@staff/ui'
import { formatAmountWithCurrency, formatDateIfValid } from '@staff/shared'
import type { DepositLoan } from '@/services/depositLoan.types'
import DataField from '@/components/DataField.vue'
import DataSection from '@/components/DataSection.vue'

const { t } = useI18n()

const props = defineProps<{ depositLoan: DepositLoan }>()

const collateralTypeLabel = computed(() => {
  const property = props.depositLoan.collateralProperty
  return property ? t(`PROPERTY_TYPE.${property.type}`) : '-'
})

const fullAddress = computed(() => {
  const property = props.depositLoan.collateralProperty
  if (!property) return '-'
  return property.streetNumber ? `${property.street} ${property.streetNumber}` : property.street
})
</script>

<style scoped lang="scss">
.card-columns {
  display: flex;
  max-width: 100%;
  flex-direction: column;
  gap: var(--space-20);
}

// Cards read better in a narrow column on wide screens; full width below that.
@media (min-width: 80rem) {
  .card-columns {
    max-width: 50%;
  }
}

</style>
